import { useParams, Navigate } from "react-router-dom";
import PageLayout from "@/components/feature/PageLayout";
import ScrollReveal from "@/components/base/ScrollReveal";
import { formatWorksWith, therapistProfilePath, teamMembers } from "@/data/therapists";
import TeamPortraitStage from "@/components/base/TeamPortraitStage";

export default function TherapistBio({ profileId }: { profileId?: string }) {
  const params = useParams<{ id: string }>();
  const id = profileId ?? params.id;
  const member = teamMembers.find((m) => m.id === id);

  if (!member) {
    return <Navigate to="/therapists" replace />;
  }

  const isClinical = member.type === "clinical";
  const isAssociate = member.role === "Associate Psychotherapist";
  const worksWithLabel = formatWorksWith(member.worksWith);
  const canonicalUrl = `https://perspectivetherapy.com${therapistProfilePath(member.id)}`;

  return (
    <>
      <title>
        {member
          ? `${member.name} | Perspective Therapy Larchmont NY`
          : "Therapist | Perspective Therapy"}
      </title>
      <meta
        name="description"
        content={
          member
            ? `Learn about ${member.name}, ${member.role} at Perspective Therapy. ${
                member.focus ||
                "Providing psychotherapy in Larchmont, NY and via telehealth across NY, NJ, IN."
              }`
            : "Browse licensed therapists at Perspective Therapy in Larchmont, NY."
        }
      />
      <link rel="canonical" href={canonicalUrl} />
      <meta property="og:title" content={`${member.name} | Perspective Therapy`} />
      <meta property="og:description" content={member.shortBio} />
      <meta property="og:type" content="profile" />
      <meta property="og:url" content={canonicalUrl} />

      <PageLayout>
        {/* Hero strip */}
        <section className="w-full bg-[#FBF9F6]">
          <div className="max-w-[1200px] mx-auto px-6 md:px-12 lg:px-20 pt-12 md:pt-16 pb-12">
            <a
              href="/therapists"
              className="inline-flex items-center gap-1.5 font-manrope text-[13px] font-medium text-[#2B2B2B]/60 hover:text-[#2B2B2B] transition-colors cursor-pointer whitespace-nowrap mb-6"
            >
              <i className="ri-arrow-left-line"></i> All Team Members
            </a>

            <div className="flex flex-col xl:flex-row gap-6 lg:gap-8 items-start">
              {/* Left group: Photo + Details side by side on desktop */}
              <div className="flex flex-col md:flex-row gap-5 lg:gap-6 items-start w-full lg:flex-1">
                {/* Photo */}
                <div className="shrink-0 w-full md:w-[360px] lg:w-[380px] xl:w-[400px]">
                  <ScrollReveal variant="fadeUp" delay={0}>
                    <div className="bg-white rounded-2xl shadow-[0_2px_16px_rgba(0,0,0,0.05)] p-4 pb-0 overflow-hidden">
                      <TeamPortraitStage src={member.img} name={member.name} variant="profile" />
                    </div>
                  </ScrollReveal>
                </div>

                {/* Name, credentials, role, availability, CTA */}
                <div className="flex-1 min-w-0">
                  <ScrollReveal variant="fadeUp" delay={50}>
                    <div className="flex flex-col gap-1">
                      {isClinical && member.availability && (
                        <p
                          className="font-manrope text-[13px] font-semibold tracking-widest uppercase"
                          style={{ color: member.accentColor }}
                        >
                          {member.availability}
                        </p>
                      )}

                      <h1 className="font-fraunces text-[#2B2B2B] text-3xl md:text-4xl font-light leading-snug">
                        {member.name}
                      </h1>

                      {member.credentials?.trim() && (
                        <p className="font-manrope text-[15px] text-[#2B2B2B]/60">
                          {member.credentials}
                        </p>
                      )}

                      <p
                        className="font-manrope text-[15px] font-semibold"
                        style={{ color: member.accentColor }}
                      >
                        {member.role}
                      </p>

                      {/* CTAs */}
                      <div className="flex flex-col gap-2 mt-3">
                        <a
                          href={member.ctaLink || "/contact"}
                          target={member.ctaLink?.startsWith("http") ? "_blank" : undefined}
                          rel={member.ctaLink?.startsWith("http") ? "noopener noreferrer" : undefined}
                          className="inline-flex items-center justify-center gap-2 text-white font-manrope font-semibold text-[15px] px-7 py-3 rounded-full transition-colors cursor-pointer whitespace-nowrap w-full md:w-auto"
                          style={{ background: member.accentColor }}
                          onMouseEnter={(e) => {
                            (e.currentTarget as HTMLAnchorElement).style.opacity = "0.88";
                          }}
                          onMouseLeave={(e) => {
                            (e.currentTarget as HTMLAnchorElement).style.opacity = "1";
                          }}
                        >
                          {member.ctaText || "Contact"} <i className="ri-arrow-right-line"></i>
                        </a>

                        {!isClinical && (
                          <a
                            href="/contact"
                            className="inline-flex items-center justify-center gap-2 font-manrope font-semibold text-[15px] px-7 py-3 rounded-full transition-colors cursor-pointer whitespace-nowrap w-full md:w-auto border"
                            style={{
                              color: "#2B2B2B",
                              borderColor: "rgba(43,43,43,0.18)",
                            }}
                            onMouseEnter={(e) => {
                              (e.currentTarget as HTMLAnchorElement).style.background = "#2B2B2B";
                              (e.currentTarget as HTMLAnchorElement).style.color = "#fff";
                            }}
                            onMouseLeave={(e) => {
                              (e.currentTarget as HTMLAnchorElement).style.background = "transparent";
                              (e.currentTarget as HTMLAnchorElement).style.color = "#2B2B2B";
                            }}
                          >
                            Get Matched with a Therapist
                          </a>
                        )}
                      </div>
                    </div>
                  </ScrollReveal>
                </div>
              </div>

              {/* Session Details tile */}
              {isClinical && (
                <div className="w-full xl:w-[380px] shrink-0">
                  <ScrollReveal variant="fadeUp" delay={100}>
                    <div
                      className="bg-white rounded-2xl border shadow-[0_1px_8px_rgba(0,0,0,0.04)] p-5 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md"
                      style={{ borderColor: member.accentColor + "22" }}
                    >
                      <h4 className="font-fraunces text-[#2B2B2B] text-lg font-medium mb-4">
                        Session Details
                      </h4>

                      <div className="flex flex-col gap-0">
                        {/* Session length */}
                        <div className="flex items-start gap-3 pb-3 mb-3 border-b border-[#2B2B2B]/8">
                          <span className="w-5 h-5 flex items-center justify-center shrink-0 mt-0.5">
                            <i
                              className="ri-time-line text-sm"
                              style={{ color: member.accentColor }}
                            ></i>
                          </span>
                          <div>
                            <p className="font-manrope text-[14px] font-semibold text-[#2B2B2B]">
                              45-50 minute sessions
                            </p>
                            <p className="font-manrope text-[13px] text-[#2B2B2B]/60">
                              Individual appointments
                            </p>
                          </div>
                        </div>

                        {/* Rates */}
                        <div className="flex items-start gap-3 pb-3 mb-3 border-b border-[#2B2B2B]/8">
                          <span className="w-5 h-5 flex items-center justify-center shrink-0 mt-0.5">
                            <i
                              className="ri-money-dollar-circle-line text-sm"
                              style={{ color: member.accentColor }}
                            ></i>
                          </span>
                          <div>
                            <p className="font-manrope text-[14px] font-semibold text-[#2B2B2B]">
                              {member.individualPrice} / individual session
                            </p>

                            {isAssociate && (
                              <div className="font-manrope text-[13px] text-[#2B2B2B]/60 mt-0.5">
                                <p>Sliding scale available</p>
                                <p>Accepts Loveland Foundation Vouchers</p>
                              </div>
                            )}

                            {member.couplesPrice && (
                              <div className="mt-2">
                                <p className="font-manrope text-[14px] font-semibold text-[#2B2B2B]">
                                  {member.couplesPrice} / couples session
                                </p>
                                <p className="font-manrope text-[13px] text-[#2B2B2B]/60 mt-0.5">
                                  25-minute extension available for $100
                                </p>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Location */}
                        {member.scheduleNote && (
                          <div className="flex items-start gap-3 pb-3 mb-3 border-b border-[#2B2B2B]/8">
                            <span className="w-5 h-5 flex items-center justify-center shrink-0 mt-0.5">
                              <i
                                className="ri-map-pin-line text-sm"
                                style={{ color: member.accentColor }}
                              ></i>
                            </span>
                            <div>
                              <p className="font-manrope text-[14px] font-semibold text-[#2B2B2B]">
                                Location
                              </p>
                              <p className="font-manrope text-[13px] text-[#2B2B2B]/60">
                                {member.scheduleNote}
                              </p>
                            </div>
                          </div>
                        )}

                        {/* Works with */}
                        <div className="flex items-start gap-3">
                          <span className="w-5 h-5 flex items-center justify-center shrink-0 mt-0.5">
                            <i
                              className="ri-user-heart-line text-sm"
                              style={{ color: member.accentColor }}
                            ></i>
                          </span>
                          <div>
                            <p className="font-manrope text-[14px] font-semibold text-[#2B2B2B]">
                              Works with
                            </p>
                            <p className="font-manrope text-[13px] text-[#2B2B2B]/60">
                              {worksWithLabel}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </ScrollReveal>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Bio and Details */}
        <section className="w-full py-16 md:py-20">
          <div className="w-full px-6 md:px-12 lg:px-20">
            <div className="grid grid-cols-1 xl:grid-cols-[1fr_340px] gap-12 xl:gap-20">
              {/* Left: Bio + Approach */}
              <div>
                <ScrollReveal variant="fadeUp" delay={0}>
                  <h2 className="font-fraunces text-[#2B2B2B] text-2xl md:text-3xl font-light leading-snug mb-5">
                    About {member.name.split(" ")[0]}
                  </h2>

                  {member.fullBio.split("\n\n").map((para, i) => (
                    <p
                      key={i}
                      className="font-manrope text-[#2B2B2B]/80 text-[15px] leading-relaxed mb-5"
                    >
                      {para}
                    </p>
                  ))}
                </ScrollReveal>

                {isClinical && member.approach && (
                  <ScrollReveal variant="fadeUp" delay={80}>
                    <h3 className="font-fraunces text-[#2B2B2B] text-xl font-medium mb-4 mt-4">
                      Therapeutic Approach
                    </h3>
                    <p className="font-manrope text-[#2B2B2B]/80 text-[15px] leading-relaxed mb-8">
                      {member.approach}
                    </p>
                  </ScrollReveal>
                )}
              </div>

              {/* Right: Sidebar */}
              <div className="flex flex-col gap-6">
                {/* Modalities */}
                {isClinical && member.modalities.length > 0 && (
                  <ScrollReveal variant="fadeUp" delay={80}>
                    <div
                      className="rounded-2xl p-6 border bg-white/80 transition-all duration-300 hover:shadow-[0_4px_24px_rgba(0,0,0,0.06)]"
                      style={{ borderColor: member.accentColor + "22" }}
                    >
                      <h4 className="font-fraunces text-[#2B2B2B] text-lg font-medium mb-4">
                        Modalities
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {member.modalities.map((m) => (
                          <span
                            key={m}
                            className="font-manrope text-[13px] font-medium px-3.5 py-1.5 rounded-full cursor-default transition-all duration-200 hover:scale-[1.03]"
                            style={{
                              color: "rgba(43,43,43,0.75)",
                              background: "rgba(203,191,175,0.30)",
                              border: "1px solid rgba(203,191,175,0.5)",
                            }}
                            onMouseEnter={(e) => {
                              (e.currentTarget as HTMLSpanElement).style.background =
                                "rgba(203,191,175,0.50)";
                              (e.currentTarget as HTMLSpanElement).style.borderColor =
                                "rgba(139,115,85,0.55)";
                            }}
                            onMouseLeave={(e) => {
                              (e.currentTarget as HTMLSpanElement).style.background =
                                "rgba(203,191,175,0.30)";
                              (e.currentTarget as HTMLSpanElement).style.borderColor =
                                "rgba(203,191,175,0.5)";
                            }}
                          >
                            {m}
                          </span>
                        ))}
                      </div>
                    </div>
                  </ScrollReveal>
                )}

                {/* Areas of Focus */}
                {isClinical && member.specialties.length > 0 && (
                  <ScrollReveal variant="fadeUp" delay={160}>
                    <div
                      className="rounded-2xl p-6 border bg-white/80 transition-all duration-300 hover:shadow-[0_4px_24px_rgba(0,0,0,0.06)]"
                      style={{ borderColor: member.accentColor + "22" }}
                    >
                      <h4 className="font-fraunces text-[#2B2B2B] text-lg font-medium mb-4">
                        Areas of Focus
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {member.specialties.map((s) => (
                          <span
                            key={s}
                            className="font-manrope text-[13px] font-semibold px-3.5 py-1.5 rounded-full cursor-default transition-all duration-200 hover:scale-[1.03]"
                            style={{ color: member.tagColor, background: member.tagBg }}
                          >
                            {s}
                          </span>
                        ))}
                      </div>
                    </div>
                  </ScrollReveal>
                )}

                {/* Languages */}
                {member.languages && member.languages.length > 0 && (
                  <ScrollReveal variant="fadeUp" delay={240}>
                    <div
                      className="rounded-2xl p-6 border bg-white/80 transition-all duration-300 hover:shadow-[0_4px_24px_rgba(0,0,0,0.06)]"
                      style={{ borderColor: member.accentColor + "22" }}
                    >
                      <h4 className="font-fraunces text-[#2B2B2B] text-lg font-medium mb-3">
                        Languages
                      </h4>
                      <p className="font-manrope text-[#2B2B2B]/80 text-[15px] leading-relaxed">
                        {member.languages.join(" · ")}
                      </p>
                    </div>
                  </ScrollReveal>
                )}

                {/* Primary CTA */}
                <ScrollReveal variant="fadeUp" delay={320}>
                  <a
                    href={member.ctaLink || "/contact"}
                    target={member.ctaLink?.startsWith("http") ? "_blank" : undefined}
                    rel={member.ctaLink?.startsWith("http") ? "noopener noreferrer" : undefined}
                    className="inline-flex items-center justify-center gap-2 text-white font-manrope font-semibold text-[15px] px-7 py-4 rounded-full transition-colors cursor-pointer whitespace-nowrap w-full"
                    style={{ background: member.accentColor }}
                    onMouseEnter={(e) => {
                      (e.currentTarget as HTMLAnchorElement).style.opacity = "0.88";
                    }}
                    onMouseLeave={(e) => {
                      (e.currentTarget as HTMLAnchorElement).style.opacity = "1";
                    }}
                  >
                    {member.ctaText || "Contact"} <i className="ri-arrow-right-line"></i>
                  </a>
                </ScrollReveal>

                {/* Secondary CTA for support staff */}
                {!isClinical && (
                  <ScrollReveal variant="fadeUp" delay={400}>
                    <a
                      href="/contact"
                      className="inline-flex items-center justify-center gap-2 font-manrope font-semibold text-[15px] px-7 py-4 rounded-full transition-colors cursor-pointer whitespace-nowrap w-full border"
                      style={{
                        color: "#2B2B2B",
                        borderColor: "rgba(43,43,43,0.18)",
                      }}
                      onMouseEnter={(e) => {
                        (e.currentTarget as HTMLAnchorElement).style.background = "#2B2B2B";
                        (e.currentTarget as HTMLAnchorElement).style.color = "#fff";
                      }}
                      onMouseLeave={(e) => {
                        (e.currentTarget as HTMLAnchorElement).style.background = "transparent";
                        (e.currentTarget as HTMLAnchorElement).style.color = "#2B2B2B";
                      }}
                    >
                      Get Matched with a Therapist
                    </a>
                  </ScrollReveal>
                )}

                {/* Back link */}
                <ScrollReveal variant="fadeIn" delay={440}>
                  <a
                    href="/therapists"
                    className="inline-flex items-center gap-1.5 font-manrope text-[13px] font-medium text-[#2B2B2B]/55 hover:text-[#2B2B2B] transition-colors cursor-pointer whitespace-nowrap justify-center"
                  >
                    <i className="ri-arrow-left-line"></i> Back to Our Team
                  </a>
                </ScrollReveal>
              </div>
            </div>
          </div>
        </section>
      </PageLayout>
    </>
  );
}
